<template>
  <div class="message-wrapper" :class="[message.role, { 'no-animation': !settings.animationEnabled }]">
    <!-- 头像区域 -->
    <div class="avatar" :style="getAvatarStyle()">
      <img
        v-if="message.role === 'assistant' && settings.aiAvatarImage"
        :src="settings.aiAvatarImage"
        class="avatar-img"
        alt="AI头像"
      >
      <div v-else-if="message.role === 'assistant'" class="avatar-icon">{{ settings.aiAvatar || '🤖' }}</div>
      
      <img
        v-else-if="settings.userAvatarImage"
        :src="settings.userAvatarImage"
        class="avatar-img"
        alt="用户头像"
      >
      <div v-else class="avatar-icon">{{ settings.userAvatar || '👤' }}</div>
    </div>
    
    <!-- 消息内容区域 -->
    <div class="message-content" :style="messageContentStyle">
      <!-- 消息内容 -->
      <div :class="message.role === 'assistant' ? 'mimo-style' : 'user-style'" :style="messageTextStyle">
        <span class="message-text" v-html="formattedContent"></span>
      </div>
      
      <!-- 打字动画效果（仅在启用时显示） -->
      <div v-if="message.isTyping && settings.animationEnabled" class="typing-indicator">
        <span></span><span></span><span></span>
      </div>
      
      <!-- 操作按钮（仅在消息完成且启用时显示） -->
      <div v-if="!message.isTyping && settings.showActionButtons" class="action-buttons">
        <button class="action-btn copy-btn" @click="copyContent" title="复制内容">
          📋 复制
        </button>
        <button class="action-btn retry-btn" @click="retryQuestion" title="重新提问">
          🔄 重新提问
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'

export default {
  name: 'ChatMessage',
  props: ['message', 'settings', 'lastMessage'],
  data() {
    return {
      displayedContent: '',
      typingTimer: null,
      isTypingAnimation: false
    }
  },
  computed: {
    formattedContent() {
      // 如果正在打字动画，显示逐字内容
      if (this.isTypingAnimation && this.settings.animationEnabled) {
        return this.displayedContent
      }
      
      // 如果消息已完成，解析Markdown
      if (this.message.content && !this.message.isTyping) {
        return this.parseMarkdown(this.message.content)
      }
      
      // 思考状态
      if (this.message.isTyping) {
        return this.parseMarkdown(this.message.content)
      }
      
      return this.message.content || ''
    },
    
    // 消息内容容器样式
    messageContentStyle() {
      const style = {}
      
      if (this.message.role === 'assistant') {
        // AI输出样式
        const aiStyle = this.settings.aiOutputStyle || {}
        style.backgroundColor = aiStyle.backgroundColor || '#FFF8F3'
        style.color = aiStyle.textColor || '#2D2D2D'
        style.opacity = (aiStyle.opacity || 100) / 100
        style.borderRadius = aiStyle.borderRadius || '12px'
        style.borderColor = aiStyle.borderColor || '#FFE8D6'
        style.borderWidth = aiStyle.borderWidth || '1px'
        style.borderStyle = 'solid'
        
        // 如果有背景图片，应用透明度
        if (this.settings.chatBackground && this.settings.chatBackground.startsWith('url')) {
          style.background = `rgba(255, 248, 243, ${style.opacity})`
        }
      } else {
        // 用户输入样式
        const userStyle = this.settings.userInputStyle || {}
        style.backgroundColor = userStyle.backgroundColor || '#FFFFFF'
        style.color = userStyle.textColor || '#000000'
        style.opacity = (userStyle.opacity || 100) / 100
        style.borderRadius = userStyle.borderRadius || '12px'
        style.borderColor = userStyle.borderColor || '#FF6900'
        style.borderWidth = userStyle.borderWidth || '1px'
        style.borderStyle = 'solid'
      }
      
      return style
    },
    
    // 消息文本样式
    messageTextStyle() {
      const style = {}
      
      if (this.message.role === 'assistant') {
        const aiStyle = this.settings.aiOutputStyle || {}
        style.color = aiStyle.textColor || '#2D2D2D'
      } else {
        const userStyle = this.settings.userInputStyle || {}
        style.color = userStyle.textColor || '#000000'
      }
      
      return style
    }
  },
  watch: {
    'message.content': {
      handler(newContent) {
        if (this.message.role === 'assistant' && !this.message.isTyping && this.settings.animationEnabled) {
          this.startTypingAnimation(newContent)
        } else if (this.message.role === 'assistant' && !this.message.isTyping) {
          // 如果动画关闭，直接显示完整内容
          this.displayedContent = this.parseMarkdown(newContent)
        }
      },
      immediate: true
    },
    'message.isTyping': {
      handler(newIsTyping) {
        if (!newIsTyping && this.message.role === 'assistant' && this.message.content && this.settings.animationEnabled) {
          this.startTypingAnimation(this.message.content)
        } else if (!newIsTyping && this.message.role === 'assistant' && this.message.content) {
          this.displayedContent = this.parseMarkdown(this.message.content)
        }
      }
    }
  },
  methods: {
    // 获取头像样式
    getAvatarStyle() {
      const style = {}
      if (this.message.role === 'assistant' && this.settings.aiAvatarImage) {
        style.background = 'transparent'
      } else if (this.message.role === 'user' && this.settings.userAvatarImage) {
        style.background = 'transparent'
      } else if (this.message.role === 'assistant') {
        style.background = 'linear-gradient(135deg, #FF6900 0%, #FF8C42 100%)'
      } else {
        style.background = 'linear-gradient(135deg, #666 0%, #999 100%)'
      }
      return style
    },

    // 解析Markdown内容（支持代码分段）
    parseMarkdown(content) {
      if (!content) return ''
      
      try {
        // 配置marked
        marked.setOptions({
          breaks: true,
          gfm: true,
          headerIds: false,
          mangle: false,
          highlight: function(code, lang) {
            // 简单的语法高亮
            return `<pre class="code-block"><code>${code}</code></pre>`
          }
        })
        
        // 解析Markdown
        let html = marked.parse(content)
        
        // 安全净化
        html = DOMPurify.sanitize(html)
        
        // 自定义样式处理
        html = this.customizeMarkdownStyles(html)
        
        return html
      } catch (error) {
        console.error('Markdown解析错误:', error)
        return content // 如果解析失败，返回原文本
      }
    },

    // 复制内容到剪贴板
    async copyContent() {
      try {
        // 获取纯文本内容
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = this.formattedContent
        const plainText = tempDiv.textContent || tempDiv.innerText || ''
        
        await navigator.clipboard.writeText(plainText)
        
        // 显示复制成功提示
        this.showTooltip('已复制到剪贴板！', 'success')
      } catch (error) {
        console.error('复制失败:', error)
        this.showTooltip('复制失败，请手动复制', 'error')
      }
    },

    // 重新提问
    retryQuestion() {
      // 通过事件传递获取对应的用户消息
      this.$emit('retry', this.message)
    },

    // 显示提示信息
    showTooltip(message, type = 'info') {
      // 创建临时提示元素
      const tooltip = document.createElement('div')
      tooltip.className = `copy-tooltip ${type}`
      tooltip.textContent = message
      tooltip.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: ${type === 'success' ? '#4caf50' : '#f44336'};
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-size: 14px;
        animation: fadeInOut 2s ease-in-out;
      `
      
      document.body.appendChild(tooltip)
      
      setTimeout(() => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip)
        }
      }, 2000)
    },

    // 自定义Markdown样式
    
    // 自定义Markdown样式
    customizeMarkdownStyles(html) {
      // 代码块
      html = html.replace(/<pre><code>([\s\S]*?)<\/code><\/pre>/g,
        '<div class="code-block"><pre><code>$1</code></pre></div>')
      
      // 行内代码
      html = html.replace(/<code>(.*?)<\/code>/g, '<span class="inline-code">$1</span>')
      
      // 标题
      html = html.replace(/<h1>(.*?)<\/h1>/g, '<h3 class="markdown-title">$1</h3>')
      html = html.replace(/<h2>(.*?)<\/h2>/g, '<h4 class="markdown-subtitle">$1</h4>')
      
      // 列表
      html = html.replace(/<ul>/g, '<ul class="markdown-list">')
      html = html.replace(/<ol>/g, '<ol class="markdown-list">')
      html = html.replace(/<li>/g, '<li class="markdown-item">')
      
      // 引用
      html = html.replace(/<blockquote>([\s\S]*?)<\/blockquote>/g,
        '<blockquote class="markdown-quote">$1</blockquote>')
      
      // 段落
      html = html.replace(/<p>/g, '<p class="markdown-paragraph">')
      
      return html
    },
    
    // 打字动画效果
    startTypingAnimation(content) {
      if (!content || this.isTypingAnimation) return
      
      this.isTypingAnimation = true
      this.displayedContent = ''
      
      const chars = content.split('')
      let currentIndex = 0
      
      // 清除之前的定时器
      if (this.typingTimer) {
        clearTimeout(this.typingTimer)
      }
      
      const typeNextChar = () => {
        if (currentIndex < chars.length) {
          // 添加字符
          this.displayedContent += chars[currentIndex]
          currentIndex++
          
          // 根据字符类型调整速度
          let delay = 30 // 默认速度
          const currentChar = chars[currentIndex - 1]
          
          if (currentChar === '。' || currentChar === '，' || currentChar === '！' || currentChar === '？') {
            delay = 100 // 标点符号稍慢
          } else if (currentChar === '\n') {
            delay = 150 // 换行更慢
          } else if (currentChar === ' ') {
            delay = 10 // 空格更快
          }
          
          // 随机变化增加真实感
          delay = delay + Math.random() * 20
          
          this.typingTimer = setTimeout(typeNextChar, delay)
          
          // 每打几个字符就滚动一次，确保可见
          if (currentIndex % 3 === 0) {
            this.$nextTick(() => {
              this.scrollToBottom()
            })
          }
        } else {
          // 动画完成
          this.isTypingAnimation = false
          this.displayedContent = this.parseMarkdown(content)
          // 动画完成后滚动到底部
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }
      }
      
      typeNextChar()
    },
    
    // 滚动到底部
    scrollToBottom() {
      // 通过事件通知父组件滚动
      this.$emit('scroll-to-bottom')
    },
    
    // Mimo式语气词库
    getToneWord() {
      const toneWords = ['嗨～', '嗯嗯', '哈哈', '嘿嘿', '哇', '原来如此', '我明白啦'];
      return toneWords[Math.floor(Math.random() * toneWords.length)];
    },
    
    // 智能表情符号插入
    getEmoji(content) {
      const emojiMap = {
        '开心': '😊', '高兴': '😄', '谢谢': '🙏', '明白': '👌',
        '疑问': '🤔', '惊讶': '😲', '鼓励': '💪', '建议': '💡'
      };
      
      for (let key in emojiMap) {
        if (content.includes(key)) {
          return emojiMap[key];
        }
      }
      
      // 根据情感分析结果返回
      if (content.length > 20) return '📝';
      if (content.includes('?') || content.includes('？')) return '🤔';
      return '✨';
    }
  },
  
  mounted() {
    // 如果是助手消息且已完成，立即开始动画
    if (this.message.role === 'assistant' && !this.message.isTyping && this.message.content) {
      this.startTypingAnimation(this.message.content)
    }
  },
  
  beforeDestroy() {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer)
    }
  }
}
</script>

<style scoped>
.message-wrapper {
  display: flex;
  margin: 16px 0;
  align-items: flex-start;
}

/* 当动画关闭时，移除所有动画效果 */
.message-wrapper.no-animation {
  animation: none !important;
  transition: none !important;
}

.message-wrapper.assistant {
  flex-direction: row;
}

.message-wrapper.user {
  flex-direction: row-reverse;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin: 0 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #FF6900 0%, #FF8C42 100%);
  color: white;
  font-size: 20px;
}

.avatar-icon {
  line-height: 1;
}

.avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  position: relative;
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 移除原有的固定样式，改为使用动态样式 */

.mimo-style {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 15px;
  line-height: 1.6;
  flex-wrap: wrap;
}

.user-style {
  font-size: 15px;
  line-height: 1.6;
}

.message-text {
  flex: 1;
  min-width: 0;
}

/* Markdown样式 - 助手消息 - 使用动态文字颜色 */
.message-wrapper.assistant .message-text >>> .markdown-title {
  font-size: 18px;
  font-weight: 700;
  margin: 8px 0;
  color: inherit;
}

.message-wrapper.assistant .message-text >>> .markdown-subtitle {
  font-size: 16px;
  font-weight: 600;
  margin: 6px 0;
  color: inherit;
}

.message-wrapper.assistant .message-text >>> .markdown-paragraph {
  margin: 6px 0;
  line-height: 1.6;
  color: inherit;
}

.message-wrapper.assistant .message-text >>> .markdown-list {
  margin: 6px 0;
  padding-left: 20px;
  color: inherit;
}

.message-wrapper.assistant .message-text >>> .markdown-item {
  margin: 4px 0;
  line-height: 1.5;
  color: inherit;
}

.message-wrapper.assistant .message-text >>> .markdown-quote {
  border-left: 3px solid currentColor;
  padding-left: 12px;
  margin: 8px 0;
  font-style: italic;
  opacity: 0.8;
  color: inherit;
}

.message-wrapper.assistant .message-text >>> .code-block {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 12px;
  margin: 8px 0;
  overflow-x: auto;
  border: 1px solid currentColor;
  opacity: 0.2;
}

.message-wrapper.assistant .message-text >>> .code-block pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Courier New', 'Consolas', monospace;
  font-size: 13px;
  color: inherit;
}

.message-wrapper.assistant .message-text >>> .inline-code {
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: inherit;
  opacity: 0.8;
}

/* Markdown样式 - 用户消息 */
.message-wrapper.user .message-text >>> .markdown-title {
  font-size: 18px;
  font-weight: 700;
  margin: 8px 0;
  color: #333;
}

.message-wrapper.user .message-text >>> .markdown-subtitle {
  font-size: 16px;
  font-weight: 600;
  margin: 6px 0;
  color: #333;
}

.message-wrapper.user .message-text >>> .markdown-paragraph {
  margin: 6px 0;
  line-height: 1.6;
  color: #333;
}

.message-wrapper.user .message-text >>> .markdown-quote {
  border-left: 3px solid rgba(0, 0, 0, 0.3);
  padding-left: 12px;
  margin: 8px 0;
  font-style: italic;
  color: #666;
}

.message-wrapper.user .message-text >>> .code-block {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 12px;
  margin: 8px 0;
  overflow-x: auto;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.message-wrapper.user .message-text >>> .inline-code {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #333;
}

/* 打字动画 - 使用动态颜色 */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 8px 0;
}

.typing-indicator span {
  width: 6px;
  height: 6px;
  background: currentColor;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
  opacity: 0.6;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

/* 操作按钮 - 固定样式，不受动态颜色影响 */
.action-buttons {
  display: flex;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: 8px;
}

.action-btn {
  padding: 6px 12px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  background: rgba(0, 0, 0, 0.05);
  color: #333;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 0, 0, 0.3);
  transform: translateY(-1px);
}

/* 动画 */
@keyframes messageSlide {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
  20% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
}

/* 移动端适配 */
@media (max-width: 480px) {
  .message-content {
    max-width: 80%;
    padding: 10px 14px;
  }
  
  .mimo-style, .user-style {
    font-size: 14px;
  }
  
  .avatar {
    width: 36px;
    height: 36px;
    margin: 0 8px;
  }
  
  .action-buttons {
    flex-wrap: wrap;
  }
  
  .action-btn {
    padding: 4px 8px;
    font-size: 11px;
  }
}
</style>